import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Observable } from 'rxjs';
import { IconComponent } from '../icon/icon';
import { GENERIC_ERROR } from '../../core/http/read-error';

export interface ConfirmDialogData {
  readonly title: string;
  readonly message: string;
  readonly confirmLabel: string;
  readonly cancelLabel?: string;
  /** Style the confirm button as a destructive action (red). */
  readonly destructive?: boolean;
  /**
   * The write to perform on Confirm. When supplied the dialog runs it itself and
   * closes only once it has completed — so a failed write leaves the dialog open
   * with whatever the user typed still there (Tier 2 §4b.1). Put any optimistic
   * state update in a `tap` on the returned stream: the close happens after the
   * stream completes, so the update has already landed.
   *
   * Omit it for a plain yes/no question — the dialog then closes with `true` on
   * Confirm and the caller does the work off `afterClosed()`.
   */
  readonly confirm?: () => Observable<unknown>;
  /** Turns a failed `confirm()` into the message shown inside the dialog. */
  readonly errorMessage?: (err: unknown) => string;
  /** Called on close by any route — Confirm-success, Cancel, X, Escape, overlay (§4b.3). */
  readonly resetOnClose?: () => void;
}

const DEFAULT_ERROR = GENERIC_ERROR;

/**
 * Generic confirmation modal, built to the Tier 2 §4b Ops action-dialog standard:
 * it closes only after a successful write, stays dismissable at every moment
 * (Cancel, X, Escape, overlay — never `disableClose`), and resets the caller's
 * form on close whichever route closed it.
 *
 * Open with `MatDialog.open(ConfirmDialogComponent, { data })`; the dialog closes
 * with `true` when the action succeeded, `false`/`undefined` otherwise.
 */
@Component({
  selector: 'app-confirm-dialog',
  imports: [
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    MatProgressSpinnerModule,
    IconComponent,
  ],
  templateUrl: './confirm-dialog.html',
  styleUrl: './confirm-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmDialogComponent {
  protected readonly data = inject<ConfirmDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject<MatDialogRef<ConfirmDialogComponent, boolean>>(MatDialogRef);

  /** True while `confirm()` is in flight. The dialog stays open and dismissable. */
  protected readonly pending = signal(false);
  /** A failed write, shown in the dialog rather than on a toast behind it. */
  protected readonly error = signal<string | null>(null);

  private closed = false;

  constructor() {
    // Every dismissal route lands here — Cancel, X, Escape, overlay click and our
    // own post-write close all go through the ref, so the reset has one home.
    // `beforeClosed` rather than `afterClosed`: the latter emits after this
    // component is destroyed.
    this.dialogRef.beforeClosed().subscribe(() => {
      this.closed = true;
      this.data.resetOnClose?.();
    });
  }

  protected confirm(): void {
    const write = this.data.confirm;
    if (!write) {
      this.close(true);
      return;
    }
    if (this.pending()) return;

    this.pending.set(true);
    this.error.set(null);
    // Deliberately not tied to this component's lifetime: dismissing mid-flight
    // must not abort a write the server has already started.
    write().subscribe({
      error: (err: unknown) => {
        this.pending.set(false);
        this.error.set(this.data.errorMessage?.(err) ?? DEFAULT_ERROR);
      },
      complete: () => {
        this.pending.set(false);
        this.close(true);
      },
    });
  }

  protected dismiss(): void {
    this.close(false);
  }

  private close(result: boolean): void {
    if (this.closed) return;
    this.dialogRef.close(result);
  }
}
